// frontend/src/components/Footer.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaPhone, FaEnvelope, FaGlobe, FaInstagram, FaYoutube, FaRegCopyright, FaStar, FaShieldAlt, FaChartLine, FaGem } from 'react-icons/fa';
import { motion } from 'framer-motion';
import axios from 'axios';
import icon1 from '../assets/icons/13892616.png';
import icon2 from '../assets/icons/11242222.png';
import icon3 from '../assets/icons/171352-200.png';
import icon4 from '../assets/icons/3135690.png';

const API_URL = 'https://api.marketmaestroovip.in';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  const [investorCharterLink, setInvestorCharterLink] = useState('');

  // Fetch Investor Charter link from DB
  useEffect(() => {
    const fetchCharter = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/policies`);
        const link = res.data?.data?.investor_charter_link || '';
        setInvestorCharterLink(link);
      } catch (err) {
        console.error('Error fetching investor charter link:', err);
      }
    };
    fetchCharter();
  }, []);

  const handleScrollNavigation = (scrollTo) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo } });
    } else {
      const element = document.getElementById(scrollTo);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 pt-16 pb-10 overflow-hidden">
      {/* Stock Market Themed Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Stock Market Charts - Top Left */}
        <motion.div
          animate={{ 
            x: [0, 20, 0],
            y: [0, -15, 0],
            rotate: [0, 5, -5, 0]
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
          className="absolute top-10 left-5 opacity-10"
        >
          <img 
            src={icon1}
            alt="Stock Chart" 
            className="w-32"
            height={32}
            width={32}
          />
        </motion.div>
        
        {/* Candlestick Chart - Top Right */}
        <motion.div
          animate={{ 
            x: [0, -15, 0],
            y: [0, 10, 0],
            scale: [1, 1.05, 1]
          }}
          transition={{ 
            duration: 7, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute top-20 right-10 opacity-10"
        >
          <img 
            src={icon2} 
            alt="Candlestick Chart" 
            className="w-36"
            height={32}
            width={32}
          />
        </motion.div>

        {/* Market Graph - Bottom Left */}
        <motion.div
          animate={{ 
            x: [0, 10, 0],
            y: [0, -10, 0],
            rotate: [0, -3, 3, 0]
          }}
          transition={{ 
            duration: 9, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          className="absolute bottom-20 left-10 opacity-10"
        >
          <img 
            src={icon3}
            alt="Market Graph" 
            className="w-28"
            height={28}
            width={28}
          />
        </motion.div>

        {/* Stock Portfolio - Bottom Right */}
        <motion.div
          animate={{ 
            x: [0, -10, 0],
            y: [0, 15, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 6, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3
          }}
          className="absolute bottom-10 right-20 opacity-10"
        >
          <img 
            src={icon4}
            alt="Portfolio" 
            className="w-24"
            height={25}
            width={25}
          />
        </motion.div>

        {/* Animated Stock Market Pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="stockGrid" width="100" height="100" patternUnits="userSpaceOnUse">
                <path d="M 100 0 L 0 0 0 100" fill="none" stroke="#3B82F6" strokeWidth="1" />
                {/* Stock chart pattern */}
                <path 
                  d="M20,80 L40,60 L60,70 L80,50 L100,40" 
                  stroke="#8B5CF6" 
                  strokeWidth="2" 
                  fill="none"
                  strokeDasharray="5,5"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#stockGrid)" />
          </svg>
        </div>

        {/* Animated Stock Price Lines */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            {/* Rising Trend Line */}
            <motion.path
              d="M0,300 Q150,250 300,280 T600,260"
              stroke="#10B981"
              strokeWidth="2"
              fill="none"
              strokeDasharray="10,10"
              animate={{
                strokeDashoffset: [0, 20]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "linear"
              }}
            />
            
            {/* Falling Trend Line */}
            <motion.path
              d="M0,400 Q150,450 300,430 T600,450"
              stroke="#EF4444"
              strokeWidth="2"
              fill="none"
              strokeDasharray="10,10"
              animate={{
                strokeDashoffset: [20, 0]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "linear",
                delay: 1
              }}
            />
          </svg>
        </div>

        {/* Floating Stock Market Icons */}
        <motion.div
          animate={{ 
            y: [0, -25, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
          className="absolute top-1/3 left-1/3 text-4xl opacity-15"
        >
          📈
        </motion.div>
        
        <motion.div
          animate={{ 
            y: [0, 20, 0],
            rotate: [0, -180, 360]
          }}
          transition={{ 
            duration: 12, 
            repeat: Infinity,
            ease: "linear",
            delay: 2
          }}
          className="absolute bottom-1/3 right-1/3 text-5xl opacity-15"
        >
          💹
        </motion.div>

        <motion.div
          animate={{ 
            x: [0, 15, 0],
            y: [0, -10, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 4
          }}
          className="absolute top-2/4 left-2/4 text-3xl opacity-15"
        >
          📊
        </motion.div>

        

        {/* Market Volume Bars */}
        <div className="absolute bottom-0 left-0 right-0 h-32 opacity-10">
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              animate={{ 
                height: ["20%", "80%", "20%"],
                opacity: [0.3, 0.8, 0.3]
              }}
              transition={{ 
                duration: 2 + Math.random() * 2, 
                repeat: Infinity,
                delay: i * 0.2
              }}
              className={`absolute bottom-0 w-6 ${
                i % 3 === 0 ? 'bg-blue-500' : 
                i % 3 === 1 ? 'bg-green-500' : 'bg-gray-500'
              }`}
              style={{
                left: `${5 + i * 8}%`,
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <motion.div
                whileHover={{ rotate: 10, scale: 1.1 }}
                className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-500 rounded-lg flex items-center justify-center shadow-lg"
              >
                <span className="text-white font-bold text-xl">M</span>
              </motion.div>
              <div>
                <h3 className="text-xl font-bold text-white">Market Maestroo</h3>
                <p className="text-blue-300 text-sm">SEBI Registered Research Analyst</p>
              </div>
            </div>
            
            <p className="text-white text-sm mb-6 leading-relaxed">
              Empowering Indian investors with professional, compliant, and actionable 
              stock market research since 2008.
            </p>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="inline-flex items-center gap-2 text-sm bg-gray-800/50 px-4 py-2 rounded-lg"
            >
              <FaRegCopyright className="text-blue-400" />
              <span className="text-white">{currentYear} Market Maestroo Private Limited</span>
            </motion.div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h4 className="text-lg font-bold mb-6 text-white flex items-center gap-2">
              <FaChartLine className="text-blue-400 text-sm" />
              Quick Links
            </h4>
            <ul className="space-y-3">
              {[
                { to: "/terms-conditions", label: "Terms & Conditions" },
                { to: "/privacy-policy", label: "Privacy Policy" },
                { to: "/refund-policy", label: "Refund Policy" },
                { to: "/", label: "Disclaimer", scrollTo: "disclosures" },
                // Investor Charter — dynamic from DB, opens in new tab
                ...(investorCharterLink
                  ? [{
                      href: investorCharterLink,
                      label: "Investor Charter",
                      external: true
                    }]
                  : []),
                { to: "/", label: "Contact Us", scrollTo: "contact" }
              ].map((link, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-2 text-white hover:text-white transition-all duration-200"
                    >
                      <motion.span
                        whileHover={{ x: 3 }}
                        className="w-1.5 h-1.5 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                      <span className="relative overflow-hidden">
                        {link.label}
                        <span className="absolute bottom-0 left-0 w-0 h-px bg-blue-400 group-hover:w-full transition-all duration-300" />
                      </span>
                    </a>
                  ) : link.scrollTo ? (
                    <button
                      onClick={() => handleScrollNavigation(link.scrollTo)}
                      className="group flex items-center gap-2 text-white hover:text-white transition-all duration-200"
                    >
                      <motion.span
                        whileHover={{ x: 3 }}
                        className="w-1.5 h-1.5 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                      <span className="relative overflow-hidden">
                        {link.label}
                        <span className="absolute bottom-0 left-0 w-0 h-px bg-blue-400 group-hover:w-full transition-all duration-300" />
                      </span>
                    </button>
                  ) : (
                    <Link 
                      to={link.to}
                      className="group flex items-center gap-2 text-white hover:text-white transition-all duration-200"
                    >
                      <motion.span
                        whileHover={{ x: 3 }}
                        className="w-1.5 h-1.5 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                      <span className="relative overflow-hidden">
                        {link.label}
                        <span className="absolute bottom-0 left-0 w-0 h-px bg-blue-400 group-hover:w-full transition-all duration-300" />
                      </span>
                    </Link>
                  )}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Legal Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4 className="text-lg font-bold mb-6 text-white flex items-center gap-2">
              <FaShieldAlt className="text-green-400 text-sm" />
              Legal
            </h4>
            <ul className="space-y-3 mb-6">
              {[
                // { to: "/", label: "SEBI Compliance" },
                { to: "/", label: "Risk Disclosure", scrollTo: "disclosures" },
                { to: "/", label: "Member Agreement" },
                { 
                  href: "https://www.sebi.gov.in/sebiweb/other/OtherAction.do?doRecognisedFpi=yes&intmId=14",
                  label: "SEBI Registration Details",
                  external: true 
                }
              ].map((link, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-2 text-white hover:text-white transition-all duration-200"
                    >
                      <motion.span
                        whileHover={{ x: 3 }}
                        className="w-1.5 h-1.5 bg-green-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                      <span className="relative overflow-hidden">
                        {link.label}
                        <span className="absolute bottom-0 left-0 w-0 h-px bg-green-400 group-hover:w-full transition-all duration-300" />
                      </span>
                    </a>
                  ) : link.scrollTo ? (
                    <button
                      onClick={() => handleScrollNavigation(link.scrollTo)}
                      className="group flex items-center gap-2 text-white hover:text-white transition-all duration-200"
                    >
                      <motion.span
                        whileHover={{ x: 3 }}
                        className="w-1.5 h-1.5 bg-green-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                      <span className="relative overflow-hidden">
                        {link.label}
                        <span className="absolute bottom-0 left-0 w-0 h-px bg-green-400 group-hover:w-full transition-all duration-300" />
                      </span>
                    </button>
                  ) : (
                    <Link 
                      to={link.to}
                      className="group flex items-center gap-2 text-white hover:text-white transition-all duration-200"
                    >
                      <motion.span
                        whileHover={{ x: 3 }}
                        className="w-1.5 h-1.5 bg-green-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                      <span className="relative overflow-hidden">
                        {link.label}
                        <span className="absolute bottom-0 left-0 w-0 h-px bg-green-400 group-hover:w-full transition-all duration-300" />
                      </span>
                    </Link>
                  )}
                </motion.li>
              ))}
            </ul>

            {/* Registration Badge */}
            <motion.div
              whileHover={{ scale: 1.02, y: -2 }}
              className="p-4 bg-gradient-to-r from-gray-800/80 to-gray-900/80 rounded-xl border border-gray-700/50 backdrop-blur-sm"
            >
              <p className="text-sm text-white mb-1">Registration Number</p>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="inline-block"
              >
                <p className="font-bold text-blue-300 text-lg tracking-wider">INH000021757</p>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Contact & Social */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h4 className="text-lg font-bold mb-6 text-white">Connect With Us</h4>
            <div className="space-y-4 mb-8">
              {[
                { icon: <FaPhone className="text-blue-400" />, href: "tel:9319745997", text: "9319745997" },
                { icon: <FaEnvelope className="text-red-400" />, href: "mailto:support@marketmaestroo.com", text: "support@marketmaestroo.com" },
                { icon: <FaGlobe className="text-green-400" />, href: "https://www.marketmaestroo.com", text: "www.marketmaestroo.com", external: true }
              ].map((contact, index) => (
                <motion.a
                  key={index}
                  href={contact.href}
                  target={contact.external ? "_blank" : "_self"}
                  rel={contact.external ? "noopener noreferrer" : ""}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-3 group"
                >
                  <motion.div
                    whileHover={{ rotate: 10, scale: 1.1 }}
                    className="p-2 bg-gray-800/50 rounded-lg group-hover:bg-gray-800 transition-colors"
                  >
                    {contact.icon}
                  </motion.div>
                  <span className="text-white group-hover:text-white transition-colors">
                    {contact.text}
                  </span>
                </motion.a>
              ))}
            </div>

            {/* Social Media */}
            <div>
              <h5 className="font-bold mb-4 text-white">Follow Us</h5>
              <div className="flex gap-3">
                {[
                  { 
                    icon: <FaInstagram />, 
                    href: "https://instagram.com/ankitwealthmanager",
                    gradient: "from-pink-500 to-red-500",
                    emoji: "📸"
                  },
                  { 
                    icon: <FaInstagram />, 
                    href: "https://instagram.com/marketmaestroo",
                    gradient: "from-purple-500 to-pink-500",
                    emoji: "📈"
                  }, 
                  { 
                    icon: <FaYoutube />, 
                    href: "https://youtube.com/@marketmaestroo",
                    gradient: "from-red-500 to-red-600",
                    emoji: "🎥"
                  }
                ].map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.15, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                    className={`relative w-12 h-12 bg-gradient-to-r ${social.gradient} rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 group`}
                  >
                    <div className="text-white">
                      {social.icon}
                    </div>
                    {/* Hover Emoji */}
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.5 }}
                      whileHover={{ opacity: 1, y: -20, scale: 1 }}
                      className="absolute -top-8 text-xl"
                    >
                      {social.emoji}
                    </motion.div>
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Copyright Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="border-t border-gray-800 pt-10 text-center relative"
        >
          {/* Animated Divider */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
          
          <p className="text-white text-sm leading-relaxed max-w-4xl mx-auto mb-6">
            Market Maestroo Private Limited is registered with Securities and Exchange Board of India 
            (SEBI) as a Research Analyst under Registration Number INH000021757. Investments in 
            securities market are subject to market risks. Read all related documents carefully before investing.
          </p>
          
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="inline-flex items-center gap-2 text-white text-sm px-4 py-2 bg-gray-800/30 rounded-lg"
          >
            <FaRegCopyright className="text-blue-400" />
            <span> {currentYear} Market Maestroo Private Limited. All Rights Reserved.</span>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;